import { inject, Injectable } from '@angular/core';
import { lastValueFrom, take } from 'rxjs';

import {  Dropdown } from 'src/app/shared/models/uiControllers';
import { Constants } from 'src/app/shared/utils/constants';
import { ERAConstants } from '../../../environmental-risk-assessment.constants';
import { ERAPecEccAquaticBamsApiService } from './era-pec-ecc-aquatic-bams.api.service';
import { ERAPecEccAquaticEchoApiService } from './era-pec-ecc-aquatic-echo.api.service';
import { EraPecEccAquaticEu,
         EraPecEccAquaticCompound,
         EraPecEccAquaticDataTransaction,
         EraPecEccAquaticDiscipline,
         EraPecEccAquaticEEACompartment,
         EraPecEccAquaticModel,
         EraPecEccAquaticSubstancesPks,
         EraPecEccAquaticUs,
         EraPecEccAquatic,
         EraPecEccAquaticBodyGet} from '../era-pec-ecc-aquatic.model';
import { EeaGapSurfacewaterPwcScenariosApiService } from 'src/app/environmental-exposure-assessment/eea-gap/eea-gap-surfacewater-pwc/eea-gap-surfacewater-pwc-scenarios/eea-gap-surfacewater-pwc-scenarios.api.service';
import { ResponseCropXRegion } from 'src/app/shared/models/pwc/scenarios.interface';

@Injectable({
  providedIn: 'root'
})
export class ERAPecEccAquaticLogicService {

  public compartments: EraPecEccAquaticEEACompartment[] = [];
  public crops: Dropdown[] = []
  public models: EraPecEccAquaticModel[] = [];
  public modelsDropdown: Dropdown[] = [];
  public pecEccTypes: Dropdown[] = [];
  public scenarios: ResponseCropXRegion[] = [];
  public substances: Dropdown[] = [];
  public waterbodiesAquatic: Dropdown[] = [];

  private ecotoxDisciplinePk: number = 0;
  private eFateDisciplinePk: number = 0;
  private subtancePks: EraPecEccAquaticSubstancesPks = {moleculesPks: [], metabolitesPks: []};

  private bamsApiService = inject(ERAPecEccAquaticBamsApiService);
  private echoApiService = inject(ERAPecEccAquaticEchoApiService);
  private eeaGapSurfaceWaterPwcScenariosApiService = inject(EeaGapSurfacewaterPwcScenariosApiService)

  public async getCompartments(): Promise<void> {
    const source$ = this.echoApiService.getEEACompartments().pipe(take(1));
    this.compartments = await lastValueFrom(source$);
  }

  private async getCompoundsByProjectPkAndDisciplinePk( projectPk: number, disciplinePk: number ): Promise<void> {
    const source$ = this.bamsApiService.getCompoundsByProjectPkAndDisciplinePk(projectPk, disciplinePk).pipe(take(1));
    const compounds = await lastValueFrom(source$);
    this.getSubstancePks(compounds);
  }

  private getSubstancePks( compounds: EraPecEccAquaticCompound[] ): void {

    const moleculesPks : number[] = [];
    const metabolitesPks : number[] = [];

    compounds.forEach(({compoundPk,isActive})=>{
      if(isActive)
        moleculesPks.push(compoundPk);
      else
        metabolitesPks.push(compoundPk);
      });

      this.subtancePks = {moleculesPks: moleculesPks, metabolitesPks: metabolitesPks};
  }

  private async getSubstances( substancesPks: EraPecEccAquaticSubstancesPks ): Promise<void> {
    this.substances.length = 0;
    const source$ = this.echoApiService.getSubstancesByPks(substancesPks).pipe(take(1));
    const substances = await lastValueFrom(source$);
    this.transformAndOrderSubstancesIntoDropdownOptions(substances);
  }

  private transformAndOrderSubstancesIntoDropdownOptions( substances: string[] ): void {
    substances.forEach((substance) => {
      this.substances.push({
        optionLabel: substance,
        optionValue: substance
      });
    });

    this.substances.sort((a, b) => {
      return (a.optionLabel as string).localeCompare((b.optionLabel as string));
    });
  }

  private async getModelsByCompartmentPkAndGeography( compartmentPk: number, geographyPk: number, geographyType: string ): Promise<void> {
    this.modelsDropdown.length = 0;
    const source$ = this.bamsApiService.getModelsByCompartmentPkAndGeography(compartmentPk, geographyPk, geographyType, this.eFateDisciplinePk).pipe(take(1));
    this.models = await lastValueFrom(source$);
    this.transformAndOrderModelsIntoDropdownOptions();
  }

  private transformAndOrderModelsIntoDropdownOptions(): void {

    this.models.forEach(({modelPk, name}) => {
      this.modelsDropdown.push({
        optionLabel: name,
        optionValue: modelPk
      });
    });

    this.modelsDropdown.sort((a, b) => {
      return (a.optionLabel as string).localeCompare((b.optionLabel as string));
    });
  }

  private setEcotoxAndEfateDisciplinePk( disciplines: EraPecEccAquaticDiscipline[] ): void {
    const {EXPOSURE_ASSESSMENT, ECOTOXICOLOGY} = Constants.DISCIPLINE_IDENTITIES;
     this.eFateDisciplinePk = disciplines.find(({disciplineName}) => disciplineName == EXPOSURE_ASSESSMENT)!.disciplinePk;
     this.ecotoxDisciplinePk = disciplines.find(({disciplineName}) => disciplineName == ECOTOXICOLOGY)!.disciplinePk;
  }

  private async getDisciplines(): Promise<void> {
    const source$ = this.echoApiService.getDisciplines().pipe(take(1));
    const disciplines = await lastValueFrom(source$);
    this.setEcotoxAndEfateDisciplinePk(disciplines);
  }

  public async getModelsAndCompounds( geographyPk: number, geographyType: string, projectPk: number ): Promise<void>{

    const compartmentPk = this.compartments.filter( compartment => compartment.name === Constants.COMPARTMENTS.SURFACE_WATER )[0]?.compartmentPk;

    await this.getDisciplines();
    await this.getModelsByCompartmentPkAndGeography( compartmentPk, geographyPk, geographyType);
    await this.getCompoundsByProjectPkAndDisciplinePk( projectPk, this.ecotoxDisciplinePk );
    await this.getSubstances( this.subtancePks );

  }

  public async getCropsAndRegions(): Promise<void>{
    this.scenarios.length = 0;
    const source$ = this.eeaGapSurfaceWaterPwcScenariosApiService.getScenarios(Constants.GEOGRAPHYS.USA).pipe(take(1));
    this.scenarios = await lastValueFrom(source$);
    this.setCrops(this.scenarios);
  }

  private setCrops(scenarios: ResponseCropXRegion[]): void {
    const existingCrops = new Set(this.crops.map(crop => crop.optionValue));
    scenarios.forEach(scenario => {
      if (!existingCrops.has(scenario.crop)) {
        this.crops.push({optionValue: scenario.crop});
        existingCrops.add(scenario.crop);
      }
    });
  }

  private createGetBody( projectPk: number, geography: string, waterbodies: string[] = [] ): EraPecEccAquaticBodyGet {
    return {
      projectPk,
      geography,
      waterbodies
    };
  }

  public async getPecEccAquaticUsByProjectPk(projectPk: number, waterbodies: string[]): Promise<EraPecEccAquaticUs[]> {
    const body = this.createGetBody( projectPk, Constants.GEOGRAPHYS.USA, waterbodies );
    const source$ = this.bamsApiService.getPecEccAquaticByProjectPk( body ).pipe(take(1));
    const aquaticResult = await lastValueFrom(source$);
    return aquaticResult.usResults;
  }

  public async getPecEccAquaticEuByProjectPk(projectPk: number): Promise<EraPecEccAquaticEu[]> {
    const body = this.createGetBody( projectPk, Constants.GEOGRAPHYS.EUROPE_UNION );
    const source$ = this.bamsApiService.getPecEccAquaticByProjectPk( body ).pipe(take(1));
    const aquaticResult = await lastValueFrom(source$);
    return aquaticResult.euResults;
  }

  public getModelsByDisciplinePkAndGeography(disciplinePk: number, geographyPk: number, geographyType: string): void {
    this.modelsDropdown.length = 0;
    this.bamsApiService.getModelsByDisciplinePkAndGeography(disciplinePk, geographyPk, geographyType).pipe(take(1)).subscribe({
      next: (models) =>{
        this.models = models;
        models.forEach(({modelPk, name}) => {
          this.modelsDropdown.push({
            optionLabel: name,
            optionValue: modelPk
          });
        });
      }
    });
  }

  public fillDropdownOptionsFromSingleList(list: string[] | number[]): Dropdown[] {
    const options: Dropdown[] = [];
    list.forEach(item => {
      options.push({optionValue:item, optionLabel:item});
    });
    return options;
  }

  public setWaterbodyOptions( geography: string ): void {
    const waterbodiesAquatic: Dropdown[] = [];

    if( geography === Constants.GEOGRAPHYS.EUROPE_UNION){
      ERAConstants.WATERBODIES_EU_LIST.forEach( waterbody =>{
          waterbodiesAquatic.push({optionValue: waterbody, optionLabel: waterbody});
      });
    }

    if( geography === Constants.GEOGRAPHYS.USA){

      ERAConstants.WATERBODIES_AQUATIC_USA_LIST.forEach( waterbody =>{
          waterbodiesAquatic.push({optionValue: waterbody, optionLabel: waterbody});
      });

    }

    this.waterbodiesAquatic = waterbodiesAquatic;

  }

  public getCompartment(modelPk: number | undefined, geography: string): string {
    let compartment: string = '';
    if(modelPk && geography == Constants.GEOGRAPHYS.EUROPE_UNION){
      const compartmentPk = this.models.find(m => m.modelPk == modelPk)!.compartmentPk;
      compartment = this.compartments.find(c => c.compartmentPk == compartmentPk)!.name;
    }
    return compartment;
  }

  public transformGridDataToSaveFormat(transactionList: any, geographyName: string, projectPk: number, type: string): EraPecEccAquatic {

    const euResults: EraPecEccAquaticEu[] = [];
    const usaResults: EraPecEccAquaticUs[] = [];
    if( geographyName === Constants.GEOGRAPHYS.EUROPE_UNION ){
      transactionList.forEach((transaction: any) =>{
        const { row } = transaction;
        const aquaticModel: EraPecEccAquaticEu = {
          resultSurfaceWaterPk: row.resultSurfaceWaterPk? row.resultSurfaceWaterPk : -1,
          projectPk: projectPk,
          modelPk: this.modelsDropdown.find(x => x.optionLabel == row.modelName)?.optionValue as number ?? undefined,
          scenarioFocus: row.scenarioFocus,
          scenarios: row.scenarios,
          bufferDistance: row.bufferDistance,
          nozzleDriftReduction: row.nozzleDriftReduction,
          dataSet: row.dataSet,
          applicationScheme: row.applicationScheme,
          geographyType: geographyName,
          period: row.period,
          waterbody: row.waterbody,
          maxPecSw: row.maxPecSw,
          pecSw21d: row.pecSw21d,
          maxPecSed: row.maxPecSed,
          dominantEnrtyRoute: row.dominantEnrtyRoute,
          globalMaxSwDate: row.globalMaxSwDate,
          applicationDate: row.applicationDate,
          substance: row.substance,
          bbchEarliest : row.bbchEarliest,
          deletable: true,
          eraSmeSelection: row.eraSmeSelection,
          eraManualSelection: row.eraManualSelection
        }
        euResults.push(aquaticModel);
      });
    }

    if( geographyName === Constants.GEOGRAPHYS.USA ){
      transactionList.forEach((transaction: any) =>{
        const { row } = transaction;

        const waterbody = row.waterbody? row.waterbody : type === Constants.PEC_ECC_TYPES.AQUATIC ? Constants.WATERBODIES_USA.POND :
                                                         type === Constants.PEC_ECC_TYPES.TPEZ ? Constants.WATERBODIES_USA.TERRESTRIAL : Constants.WATERBODIES_USA.WETLAND;

        const aquaticModel: EraPecEccAquaticUs = {
          applicationDate: row.applicationDate,
          applicationScheme: row.applicationScheme,
          crop: row.crop,
          dataSet: row.dataSet,
          deletable: true,
          eraManualSelection: row.eraManualSelection,
          eraSmeSelection: row.eraSmeSelection,
          modelPk: this.modelsDropdown.find(x => x.optionLabel == row.modelName)?.optionValue as number ?? undefined,
          oneDayAvg: row.oneDayAvg,
          projectPk: projectPk,
          region: row.region,
          resultsSummaryPK: row.resultsSummaryPK? row.resultsSummaryPK : -1,
          sixtyDayAvg: row.sixtyDayAvg,
          substance: row.substance,
          twentyOneDayAvg: row.twentyOneDayAvg,
          waterbody: waterbody,
          tpezlba: row.tpezlba,
          wpezlba: row.wpezlba,
          wpezkgha: row.wpezkgha
        }
        usaResults.push(aquaticModel);
      });
    }

    const result: EraPecEccAquatic = {euResults: euResults, usResults: usaResults };

    return result;

  }

  public save(dataTransaction: EraPecEccAquaticDataTransaction): Promise<boolean> {
    return new Promise<boolean> ((resolve, reject) => {
      this.bamsApiService.insertUpdateAndDeleteEraPecEccAquatic(dataTransaction).subscribe({
        next: (success) =>{
          resolve(success);
        },
        error: () =>{
          reject(false);
        }
      });
    })
  }

  public isDataFromRun(data: any, fieldToCheck: string) {
    return !data.newRow && fieldToCheck !== null;
  }

  public getScenarioFocusListValues(modelName: string): string[]{
    let result: string[] = [];

    switch(modelName){
      case Constants.MODELS.STEP_1_2:
        result.push(...['Step 1', 'Step 2', 'Maximum']);
        break;
      case Constants.MODELS.SWASH:
      case Constants.MODELS.SWASH63:
        result.push(...['Step 3']);
        break;
      case Constants.MODELS.SWAN:
        result.push(...['Step 4']);
        break;
    }


    return result;
  }

  public enableColumnForStep3And4(stepFocus: string): boolean{
    let result: boolean = true;

    result = stepFocus == "Step 1" ||
              stepFocus == "Step 2" ||
              stepFocus == "Maximum" ||
              !stepFocus ? true : false;

    return result;
  }

  public enableColumnForStep2(stepFocus: string): boolean{
    let result: boolean = true;

    result = stepFocus != "Step 2" ? true : false;

    return result;
  }

  public enableColumnForStep4(stepFocus: string): boolean{
    let result: boolean = true;

    result = stepFocus != "Step 4" ? true : false;

    return result;
  }
}
